/**
 * Sign in with Apple JS (web, Discord excluded). The Apple script loads on first signIn(). Needs a Services ID
 * (clientId) with the site's domain and return URL registered in the Apple Developer account.
 *
 * Replay protection: each sign-in fetches a fresh server nonce (GET /v1/auth/nonce, `nonce` below) while the script
 * loads, and Apple gets its SHA-256 hex, as on iOS (Apple copies it into the token's `nonce` claim); the raw nonce
 * goes to the API with the token. Token revocation: the popup's `authorization.code` and the return URL go along too,
 * and the API exchanges them for the refresh token it revokes on unlink and account deletion.
 */
import { loadScript } from '../lazy.js';
import { sha256Hex } from './nonce.js';
import { randomState } from './pkce.js';
import { SignInCancelled, type IdentityProvider, type IdentityToken } from './types.js';

interface AppleIdAuth {
  init(o: { clientId: string; scope: string; redirectURI: string; state: string; nonce?: string; usePopup: boolean }): void;
  signIn(): Promise<{ authorization: { id_token: string; code: string; state?: string } }>;
}

export interface AppleSignInConfig {
  /** Services ID, e.g. com.marketpop.web */
  clientId: string;
  redirectUri: string;
  locale?: string;
  document?: Document;
  /** a fresh single-use server nonce for each sign-in (fetchAuthNonce; createProviders sets it); null signs in without one */
  nonce?: () => Promise<string | null>;
}

export class AppleIdentityProvider implements IdentityProvider {
  readonly provider = 'apple';

  constructor(private readonly c: AppleSignInConfig) {}

  async signIn(): Promise<IdentityToken> {
    const [, nonce] = await Promise.all([
      loadScript(`https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/${this.c.locale ?? 'en_US'}/appleid.auth.js`, this.c.document),
      this.c.nonce?.() ?? null,
    ]);
    const auth = (window as unknown as { AppleID: { auth: AppleIdAuth } }).AppleID.auth;
    auth.init({ clientId: this.c.clientId, scope: 'email', redirectURI: this.c.redirectUri, state: randomState(), ...(nonce ? { nonce: await sha256Hex(nonce) } : {}), usePopup: true });
    const res = await auth.signIn().catch((e: unknown) => {
      if ((e as { error?: string }).error === 'popup_closed_by_user') throw new SignInCancelled('apple');
      throw e;
    });
    const code = res.authorization.code;
    return {
      provider: 'apple',
      idToken: res.authorization.id_token,
      ...(nonce ? { nonce } : {}),
      ...(code ? { authorizationCode: code, redirectUri: this.c.redirectUri } : {}),
    };
  }
}
